import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const AdminFeedbackManager = () => {
    const [feedback, setFeedback] = useState([]);
    const [filteredFeedback, setFilteredFeedback] = useState([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({});
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [filters, setFilters] = useState({
        status: '',
        category: '',
        priority: ''
    });
    const [showReplyModal, setShowReplyModal] = useState(false);

    const applyFilters = useCallback(() => {
        let filtered = [...feedback];

        if (filters.status) {
            filtered = filtered.filter(item => item.status === filters.status);
        }
        if (filters.category) {
            filtered = filtered.filter(item => item.category === filters.category);
        }
        if (filters.priority) {
            filtered = filtered.filter(item => item.priority === filters.priority);
        }

        setFilteredFeedback(filtered);
    }, [feedback, filters]);

    const fetchFeedback = async () => {
        try {
            setLoading(true);
            const res = await api.get('/feedback');
            // Handle both old format (array) and new paginated format
            const feedbackData = res.data.success && res.data.feedback
                ? res.data.feedback.data
                : res.data;
            setFeedback(Array.isArray(feedbackData) ? feedbackData : []);
        } catch (error) {
            console.error('Error fetching feedback:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const res = await api.get('/feedback/stats/summary');
            setStats(res.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    useEffect(() => {
        fetchFeedback();
        fetchStats();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [feedback, filters, applyFilters]);

    const handleReply = async (feedbackId) => {
        if (!replyText.trim()) return;

        try {
            setLoading(true);
            await api.put(`/feedback/${feedbackId}/reply`, {
                message: replyText
            });
            setReplyText('');
            setShowReplyModal(false);
            setSelectedFeedback(null);
            fetchFeedback();
            fetchStats();
            alert('Phản hồi đã được gửi thành công!');
        } catch (error) {
            console.error('Error sending reply:', error);
            alert('Có lỗi xảy ra khi gửi phản hồi.');
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (feedbackId, newStatus) => {
        try {
            await api.put(`/feedback/${feedbackId}/status`, {
                status: newStatus
            });
            fetchFeedback();
            fetchStats();
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const deleteFeedback = async (feedbackId, subject) => {
        if (window.confirm(`Bạn có chắc chắn muốn xóa phản hồi: "${subject}"?`)) {
            try {
                await api.delete(`/feedback/${feedbackId}`);
                fetchFeedback();
                fetchStats();
                alert('Phản hồi đã được xóa thành công!');
            } catch (error) {
                console.error('Error deleting feedback:', error);
                alert('Có lỗi xảy ra khi xóa phản hồi.');
            }
        }
    };

    const openReplyModal = (feedbackItem) => {
        setSelectedFeedback(feedbackItem);
        setReplyText(feedbackItem.adminReply?.message || '');
        setShowReplyModal(true);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('vi-VN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'bg-yellow-100 text-yellow-800',
            in_progress: 'bg-blue-100 text-blue-800',
            resolved: 'bg-green-100 text-green-800',
            closed: 'bg-gray-100 text-gray-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getPriorityColor = (priority) => {
        const colors = {
            low: 'bg-green-100 text-green-700',
            medium: 'bg-yellow-100 text-yellow-700',
            high: 'bg-orange-100 text-orange-700',
            urgent: 'bg-red-100 text-red-700'
        };
        return colors[priority] || 'bg-gray-100 text-gray-700';
    };

    const categoryLabels = {
        question: 'Câu hỏi',
        feedback: 'Phản hồi',
        technical_issue: 'Vấn đề kỹ thuật',
        complaint: 'Khiếu nại',
        suggestion: 'Đề xuất',
        other: 'Khác'
    };

    const priorityLabels = {
        low: 'Thấp',
        medium: 'Trung bình',
        high: 'Cao',
        urgent: 'Khẩn cấp'
    };

    const statusLabels = {
        pending: 'Đang chờ',
        in_progress: 'Đang xử lý',
        resolved: 'Đã giải quyết',
        closed: 'Đã đóng'
    };

    return (
        <div className="space-y-6">
            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 p-4 text-white">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
                            <i className="fas fa-inbox text-lg"></i>
                        </div>
                        <div>
                            <p className="text-sm opacity-90">Tổng số</p>
                            <p className="text-xl font-bold">{stats.total || 0}</p>
                        </div>
                    </div>
                </div>
                <div className="rounded-xl bg-gradient-to-br from-yellow-500 to-yellow-600 p-4 text-white">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
                            <i className="fas fa-exclamation-circle text-lg"></i>
                        </div>
                        <div>
                            <p className="text-sm opacity-90">Chưa đọc</p>
                            <p className="text-xl font-bold">{stats.unread || 0}</p>
                        </div>
                    </div>
                </div>
                <div className="rounded-xl bg-gradient-to-br from-green-500 to-green-600 p-4 text-white">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
                            <i className="fas fa-calendar-week text-lg"></i>
                        </div>
                        <div>
                            <p className="text-sm opacity-90">Tuần này</p>
                            <p className="text-xl font-bold">{stats.recentWeek || 0}</p>
                        </div>
                    </div>
                </div>
                <div className="rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 p-4 text-white">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
                            <i className="fas fa-chart-pie text-lg"></i>
                        </div>
                        <div>
                            <p className="text-sm opacity-90">Đã xử lý</p>
                            <p className="text-xl font-bold">
                                {stats.statusBreakdown?.find(s => s._id === 'resolved')?.count || 0}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="rounded-2xl bg-slate-50/80 p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Bộ lọc</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters({...filters, status: e.target.value})}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                    >
                        <option value="">Tất cả trạng thái</option>
                        {Object.entries(statusLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                    <select
                        value={filters.category}
                        onChange={(e) => setFilters({...filters, category: e.target.value})}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                    >
                        <option value="">Tất cả danh mục</option>
                        {Object.entries(categoryLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                    <select
                        value={filters.priority}
                        onChange={(e) => setFilters({...filters, priority: e.target.value})}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                    >
                        <option value="">Tất cả độ ưu tiên</option>
                        {Object.entries(priorityLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Feedback List */}
            <div className="space-y-4">
                {loading ? (
                    <div className="text-center py-8">
                        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600"></div>
                        <p className="mt-2 text-sm text-slate-600">Đang tải...</p>
                    </div>
                ) : filteredFeedback.length === 0 ? (
                    <div className="text-center py-12 rounded-2xl bg-slate-50">
                        <div className="text-4xl mb-4">📭</div>
                        <h3 className="text-lg font-semibold text-slate-700 mb-2">Không có phản hồi nào</h3>
                        <p className="text-slate-500">Chưa có phản hồi nào phù hợp với bộ lọc đã chọn.</p>
                    </div>
                ) : (
                    filteredFeedback.map((item) => (
                        <div
                            key={item._id}
                            className={`rounded-2xl border p-6 shadow-sm hover:shadow-md transition-shadow ${
                                !item.isRead ? 'bg-indigo-50/50 border-indigo-200' : 'bg-white border-slate-200'
                            }`}
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <h3 className="text-lg font-semibold text-slate-900">
                                            {item.subject}
                                        </h3>
                                        {!item.isRead && (
                                            <span className="inline-flex h-2 w-2 rounded-full bg-indigo-500"></span>
                                        )}
                                    </div>
                                    <p className="text-sm text-slate-600 mb-3">
                                        Từ: <span className="font-medium">{item.student.name}</span> ({item.student.email})
                                    </p>
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(item.status)}`}>
                                            {statusLabels[item.status]}
                                        </span>
                                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getPriorityColor(item.priority)}`}>
                                            {priorityLabels[item.priority]}
                                        </span>
                                        <span className="inline-flex rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                                            {categoryLabels[item.category]}
                                        </span>
                                    </div>
                                </div>
                                <div className="text-right text-sm text-slate-500">
                                    {formatDate(item.createdAt)}
                                </div>
                            </div>

                            <div className="rounded-xl bg-slate-50 p-4 mb-4">
                                <p className="text-sm text-slate-700 leading-relaxed">
                                    {item.message}
                                </p>
                            </div>

                            {item.adminReply && (
                                <div className="border-t border-slate-200 pt-4 mb-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100">
                                            <i className="fas fa-reply text-xs text-indigo-600"></i>
                                        </div>
                                        <h4 className="text-sm font-semibold text-slate-900">
                                            Phản hồi từ {item.adminReply.repliedBy?.name || 'Quản trị viên'}
                                        </h4>
                                        <span className="text-xs text-slate-500">
                                            {formatDate(item.adminReply.repliedAt)}
                                        </span>
                                    </div>
                                    <div className="rounded-xl bg-indigo-50 p-4">
                                        <p className="text-sm text-slate-700 leading-relaxed">
                                            {item.adminReply.message}
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => openReplyModal(item)}
                                    className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-indigo-700"
                                >
                                    <i className="fas fa-reply"></i>
                                    {item.adminReply ? 'Chỉnh sửa phản hồi' : 'Trả lời'}
                                </button>

                                {item.status !== 'resolved' && (
                                    <button
                                        onClick={() => updateStatus(item._id, 'resolved')}
                                        className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-green-700"
                                    >
                                        <i className="fas fa-check"></i>
                                        Đánh dấu đã giải quyết
                                    </button>
                                )}

                                {item.status !== 'closed' && (
                                    <button
                                        onClick={() => updateStatus(item._id, 'closed')}
                                        className="inline-flex items-center gap-1 rounded-lg bg-gray-600 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-gray-700"
                                    >
                                        <i className="fas fa-times"></i>
                                        Đóng
                                    </button>
                                )}

                                <button
                                    onClick={() => deleteFeedback(item._id, item.subject)}
                                    className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-red-700"
                                >
                                    <i className="fas fa-trash"></i>
                                    Xóa
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Reply Modal */}
            {showReplyModal && selectedFeedback && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-semibold text-slate-900 mb-4">
                            {selectedFeedback.adminReply ? 'Chỉnh sửa phản hồi' : 'Trả lời phản hồi'}
                        </h2>

                        <div className="rounded-xl bg-slate-50 p-4 mb-4">
                            <h3 className="font-semibold text-slate-900 mb-2">{selectedFeedback.subject}</h3>
                            <p className="text-sm text-slate-600 mb-2">
                                Từ: {selectedFeedback.student.name} ({selectedFeedback.student.email})
                            </p>
                            <p className="text-sm text-slate-700">{selectedFeedback.message}</p>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                Phản hồi của bạn
                            </label>
                            <textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                rows={6}
                                maxLength={2000}
                                placeholder="Nhập phản hồi của bạn..."
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 resize-none"
                            />
                            <p className="text-xs text-slate-500 mt-1">
                                {replyText.length}/2000 ký tự
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => handleReply(selectedFeedback._id)}
                                disabled={loading || !replyText.trim()}
                                className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow transition hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {loading ? (
                                    <>
                                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></span>
                                        Đang gửi...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-paper-plane"></i>
                                        Gửi phản hồi
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => {
                                    setShowReplyModal(false);
                                    setSelectedFeedback(null);
                                    setReplyText('');
                                }}
                                className="rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-medium text-slate-700 shadow transition hover:bg-slate-50"
                            >
                                Hủy
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminFeedbackManager;